const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const IMAGE_ACCEPT = IMAGE_TYPES.join(",");
// Use decimal MB so every upload also fits within a 1 MiB limit.
export const MAX_UPLOAD_IMAGE_BYTES = 1_000_000;
export const MAX_SOURCE_IMAGE_BYTES = 30 * 1024 * 1024;

export function imageFileError(file: Pick<File, "type" | "size">): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return "Choose a PNG, JPEG, WebP, or GIF image.";
  if (!file.size) return "This image is empty. Choose another image.";
  if (file.size > MAX_SOURCE_IMAGE_BYTES) return "Choose an image up to 30 MB. It will be compressed before upload.";
  return null;
}

type DecodedImage = { source: CanvasImageSource; width: number; height: number; release: () => void };

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // Some browsers can display formats that their bitmap decoder cannot read.
    }
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("This image could not be read. Choose another image."));
      img.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, release: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function encodeImage(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob && blob.size) resolve(blob);
      else reject(new Error("This image could not be compressed. Choose another image."));
    }, type, quality);
  });
}

/** Keep small files (including animated GIFs) intact. Larger files are resized
 * and encoded locally; no original bytes are sent to Storage. Canvas exports
 * are still images, so oversized GIFs lose their animation. */
export async function compressImageFile(file: File): Promise<File> {
  const error = imageFileError(file);
  if (error) throw new Error(error);
  if (file.size <= MAX_UPLOAD_IMAGE_BYTES) return file;

  const decoded = await decodeImage(file);
  const canvas = document.createElement("canvas");
  try {
    if (!decoded.width || !decoded.height) throw new Error("This image could not be read. Choose another image.");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Image compression is unavailable in this browser. Choose an image under 1 MB.");
    const scale = Math.min(1, 1920 / Math.max(decoded.width, decoded.height));
    let width = Math.max(1, Math.round(decoded.width * scale));
    let height = Math.max(1, Math.round(decoded.height * scale));
    const type = file.type === "image/jpeg" ? "image/jpeg" : "image/webp";

    for (let attempt = 0; attempt < 12; attempt += 1) {
      canvas.width = width;
      canvas.height = height;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(decoded.source, 0, 0, width, height);
      let encoded: Blob | undefined;
      for (const quality of [0.9, 0.8, 0.7, 0.6, 0.5]) {
        encoded = await encodeImage(canvas, type, quality);
        if (encoded.size <= MAX_UPLOAD_IMAGE_BYTES) {
          const extension = encoded.type === "image/jpeg" ? "jpg" : encoded.type === "image/webp" ? "webp" : "png";
          return new File([encoded], `${file.name.replace(/\.[^.]*$/, "") || "image"}.${extension}`, { type: encoded.type, lastModified: file.lastModified });
        }
        // Browsers without WebP encoding return PNG, whose size ignores quality.
        if (encoded.type === "image/png") break;
      }
      const shrink = Math.min(0.8, Math.sqrt(MAX_UPLOAD_IMAGE_BYTES / encoded!.size) * 0.9);
      const nextWidth = Math.max(1, Math.floor(width * shrink));
      const nextHeight = Math.max(1, Math.floor(height * shrink));
      if (nextWidth === width && nextHeight === height) break;
      width = nextWidth;
      height = nextHeight;
    }
    throw new Error("This image could not be reduced to 1 MB. Choose a smaller image.");
  } finally {
    decoded.release();
    canvas.width = 0;
    canvas.height = 0;
  }
}
